import { useState, useEffect, useRef } from "react";
import { Download, Eye, FolderClosed, FolderOpen, Loader2, Upload, Trash2, ChevronRight, ChevronDown, FileText } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { PdfViewerModal } from "./PdfViewerModal";
import { SkeletonList } from "./Skeleton";

/**
 * Shows downloadable handouts for a specific Organ (1-5).
 * Auto-pulls files from the matching library folder.
 * Collapsible folder UI - default collapsed to keep focus on syllabus.
 * When isAdmin=true, shows upload + delete controls.
 */
export const OrganHandouts = ({ apiUrl, organIndex, lang = "ms", isAdmin = false }) => {
  const [files, setFiles] = useState([]);
  const [folderId, setFolderId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [expanded, setExpanded] = useState(() => {
    try {
      const stored = localStorage.getItem(`bss.library.expanded.${organIndex}`);
      return stored === "true";
    } catch (_e) {
      return false;
    }
  });
  const fileInputRef = useRef(null);

  const isEn = lang === "en";

  const toggleExpanded = () => {
    const next = !expanded;
    setExpanded(next);
    try { localStorage.setItem(`bss.library.expanded.${organIndex}`, String(next)); } catch (_e) { /* noop */ }
  };

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/organs/${organIndex}/handouts`);
      setFiles(res.data.files || []);
      setFolderId(res.data.folder?.id || null);
    } catch {
      setFiles([]);
      setFolderId(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${apiUrl}/organs/${organIndex}/handouts`)
      .then((res) => {
        if (!cancelled) {
          setFiles(res.data.files || []);
          setFolderId(res.data.folder?.id || null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFiles([]);
          setFolderId(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiUrl, organIndex]);

  const handleDownload = (file) => {
    axios.post(`${apiUrl}/library/files/${file.id}/log-download`).catch(() => {});
    const link = document.createElement("a");
    link.href = `${apiUrl}/library/files/${file.id}/download`;
    link.download = file.original_name;
    link.click();
  };

  const handleView = (file) => {
    setViewer({
      url: `${apiUrl}/library/files/${file.id}/view`,
      downloadUrl: `${apiUrl}/library/files/${file.id}/download`,
      title: file.original_name,
    });
  };

  const handleUpload = async (e) => {
    const selected = e.target.files;
    if (!selected || selected.length === 0) return;
    if (!folderId) {
      toast.error(isEn ? `No library folder found for Organ ${organIndex}` : `Folder library untuk Organ ${organIndex} tidak dijumpai`);
      return;
    }
    setUploading(true);
    try {
      for (const file of selected) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("folder_id", folderId);
        await axios.post(`${apiUrl}/library/upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(isEn ? `${selected.length} file(s) uploaded` : `${selected.length} fail dimuat naik`);
      await fetchFiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isEn ? "Upload failed" : "Gagal muat naik"));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (file) => {
    if (!window.confirm(isEn ? `Delete "${file.original_name}"?` : `Padam "${file.original_name}"?`)) return;
    try {
      await axios.delete(`${apiUrl}/library/files/${file.id}`);
      toast.success(isEn ? "File deleted" : "Fail dipadam");
      await fetchFiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isEn ? "Failed to delete" : "Gagal padam"));
    }
  };

  // Hide entirely for non-admin when no files (clean UX for students)
  if (!isAdmin && !loading && files.length === 0) return null;

  return (
    <>
      <div className="dash-card" data-testid={`organ-${organIndex}-handouts`}>
        {/* Clickable folder header to collapse/expand */}
        <button
          type="button"
          onClick={toggleExpanded}
          className="w-full dash-card-header text-left hover:bg-[#FAFAFA] transition-colors"
          data-testid={`organ-${organIndex}-handouts-toggle`}
          aria-expanded={expanded}
        >
          <h3 className="flex items-center gap-2">
            {expanded ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
            {expanded ? <FolderOpen size={16} className="text-[#0A0A0A]" /> : <FolderClosed size={16} className="text-[#0A0A0A]" />}
            {isEn ? `Reading Materials - Organ ${organIndex}` : `Bahan Bacaan - Organ ${organIndex}`}
          </h3>
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <span className="text-xs text-gray-400">
              {files.length} {isEn ? "files" : "fail"}
            </span>
            {isAdmin && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                  multiple
                  onChange={handleUpload}
                  className="hidden"
                  data-testid={`organ-${organIndex}-upload-input`}
                />
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                  disabled={uploading || loading}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold text-white bg-[#2563EB] hover:bg-[#1d4ed8] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid={`organ-${organIndex}-upload-btn`}
                >
                  {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                  {uploading
                    ? (isEn ? "Uploading…" : "Memuat naik…")
                    : (isEn ? "Upload" : "Muat Naik")}
                </button>
              </>
            )}
          </div>
        </button>

        {expanded && (
        <div className="p-3">
          {loading ? (
            <SkeletonList count={2} />
          ) : files.length === 0 ? (
            <div className="text-center py-6 text-xs text-[rgba(0,0,0,0.4)] italic">
              {isAdmin
                ? (isEn ? `No reading materials yet. Click "Upload" to add resources for Organ ${organIndex}.` : `Belum ada bahan bacaan. Klik "Muat Naik" untuk tambah bahan Organ ${organIndex}.`)
                : (isEn ? "No reading materials available yet." : "Belum ada bahan bacaan.")}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {files.map((file) => (
                <div
                  key={file.id}
                  className="flex items-center gap-3 px-3 py-3 rounded-xl border border-[rgba(0,0,0,0.06)] hover:border-[#0A0A0A] hover:shadow-[0_4px_12px_rgba(0,0,0,0.04)] transition-all duration-150 group"
                  data-testid="handout-item"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#F7F7F7] flex items-center justify-center flex-shrink-0 group-hover:bg-[#0A0A0A] transition-colors">
                    <FileText size={18} className="text-[#0A0A0A] group-hover:text-[#2563EB]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#0A0A0A] truncate" title={file.original_name}>
                      {file.original_name}
                    </p>
                    <p className="text-[10px] text-[rgba(0,0,0,0.4)]">
                      {file.size_label || ""} · PDF
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => handleView(file)}
                      className="w-8 h-8 rounded-full hover:bg-[#F7F7F7] flex items-center justify-center transition-colors"
                      title={isEn ? "Preview" : "Prebiu"}
                      data-testid="handout-view"
                    >
                      <Eye size={14} className="text-[#0A0A0A]" />
                    </button>
                    <button
                      onClick={() => handleDownload(file)}
                      className="w-8 h-8 rounded-full hover:bg-[#F7F7F7] flex items-center justify-center transition-colors"
                      title={isEn ? "Download" : "Muat turun"}
                      data-testid="handout-download"
                    >
                      <Download size={14} className="text-[#2563EB]" />
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => handleDelete(file)}
                        className="w-8 h-8 rounded-full hover:bg-red-50 flex items-center justify-center transition-colors"
                        title={isEn ? "Delete" : "Padam"}
                        data-testid="handout-delete"
                      >
                        <Trash2 size={14} className="text-red-500" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        )}
      </div>

      {viewer && (
        <PdfViewerModal
          fileUrl={viewer.url}
          downloadUrl={viewer.downloadUrl}
          title={viewer.title}
          onClose={() => setViewer(null)}
          lang={lang}
        />
      )}
    </>
  );
};
